<template>
  <div class="chart-wrapper">
    <!-- Fake multiple charts to make them update better. -->
    <template v-if="time?.name === 'Day'">
      <apexchart
        v-if="chartSeries"
        type="bar"
        :options="chartOptions"
        :series="chartSeries"
        height="100%"
        width="100%"
      />
    </template>
    <template v-if="time?.name === 'Hour'">
      <apexchart
        v-if="chartSeries"
        :key="day"
        type="bar"
        :options="chartOptions"
        :series="chartSeries"
        height="100%"
        width="100%"
      />
    </template>
    <template v-if="time?.name === '15 Min'">
      <apexchart
        v-if="chartSeries"
        :key="day"
        type="bar"
        :options="chartOptions"
        :series="chartSeries"
        height="100%"
        width="100%"
      />
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import dayjs from 'dayjs';
import { barColors } from '@/utils/chartColors';

import type { WidgetFilter, WidgetSetting, DataUniqueVisitors, UniqueGroup } from '@/types/Widget';
import type { BaseChartOptions } from '@/utils/chartSettings';
import { getChartExportSettings, getDefaultChartOptions } from '@/utils/chartSettings';
import { formatNumber } from '@/utils/formatNumbers';

const props = defineProps<{
  widget: DataUniqueVisitors;
  area?: WidgetFilter;
  time?: WidgetFilter;
  zone?: WidgetSetting;
  day?: WidgetSetting;
  cluster?: WidgetSetting;
}>();

// Get array of the Time objects. Each has an ID equal to the
// timestamp and a title equal to the text to display.
const labels = computed(() => {
  let allLabels: WidgetSetting[] = [];

  const chosenTime = props.time?.name;
  if (!props.widget) {
    return allLabels;
  }

  if (props.widget.filters && props.widget.filters.time) {
    const dataFilter = props.widget.filters.time.find((filter) => filter.name === chosenTime);

    if (dataFilter && dataFilter.data && dataFilter.data.length) {
      if (props.time?.name === 'Day') {
        allLabels = dataFilter.data;
      } else {
        const dayId = props.day?.id;
        const dataSet = dataFilter.data.find((dat) => dat.id === dayId);
        if (dataSet?.data) {
          allLabels = dataSet.data;
        }
      }
    }
  }

  return allLabels;
});

const chartSeries = computed(() => {
  const areaName = props.area?.name;
  const timeName = props.time?.name;
  const dayId = props.day?.id;

  if (!areaName || !timeName || timeName === 'Total') {
    return null;
  }

  let settingData: UniqueGroup = {};

  const zoneId = props.zone?.id;
  const clusterId = props.cluster?.id;

  if (areaName === 'Site') {
    if (timeName === 'Day') {
      settingData = props.widget.data['Site'][timeName];
    }
    if ((timeName === '15 Min' || timeName === 'Hour') && dayId) {
      settingData = props.widget.data['Site'][timeName][dayId];
    }
  } else if (areaName === 'Zone' && zoneId) {
    if (timeName === 'Day') {
      settingData = props.widget.data['Zone'][zoneId][timeName];
    }
    if ((timeName === '15 Min' || timeName === 'Hour') && dayId) {
      settingData = props.widget.data['Zone'][zoneId][timeName][dayId];
    }
  } else if (areaName === 'Cluster' && clusterId) {
    if (timeName === 'Day') {
      settingData = props.widget.data['Cluster'][clusterId][timeName];
    }
    if ((timeName === '15 Min' || timeName === 'Hour') && dayId) {
      settingData = props.widget.data['Cluster'][clusterId][timeName][dayId];
    }
  }

  if (!settingData) {
    return null;
  }

  const formatted = Object.keys(settingData).map((timeCode) => {
    return {
      title: dayjs(timeCode, 'YYYYMMDD').format('YYYY-MM-DD'),
      data: [settingData[timeCode]['Engaged Visitors'], settingData[timeCode]['Passerby Visitors']],
    };
  });

  type barData = {
    x: string;
    y: number;
  };

  const engagedData = ref<barData[]>([]);
  const passerData = ref<barData[]>([]);

  formatted.forEach((char) => {
    engagedData.value.push({
      x: 'Engaged',
      y: char.data[0],
    });
    passerData.value.push({
      x: 'Passerby',
      y: char.data[1],
    });
  });

  return [
    {
      name: 'Engaged',
      data: engagedData.value,
    },
    {
      name: 'Passerby',
      data: passerData.value,
    },
  ];
});

const defaultChartOptions = getDefaultChartOptions(props.widget.code_name);

const chartOptions = computed<BaseChartOptions>(() => {
  const categories: string[] = [];
  const notFifteen = props.time?.name !== '15 Min';

  if (labels.value) {
    Object.keys(labels.value).forEach((key) => {
      const labelObj = labels.value[key as any];
      if (labelObj.title) {
        categories.push(labelObj.title);
      }
    });
  }

  return {
    ...defaultChartOptions.value,
    chart: {
      ...defaultChartOptions.value.chart,
      type: 'bar',
      stacked: true,
      width: '100%',
      height: '100%',
      id: 'meshh-chart-unique-visitors',
      toolbar: getChartExportSettings(
        props.widget.name,
        props.area?.name || '',
        props.time?.name || '',
        props.zone?.title || '',
        props.cluster?.title || '',
        '',
      ),
    },
    colors: barColors,
    xaxis: {
      categories: categories,
      labels: {
        hideOverlappingLabels: true,
        rotate: 0,
      },
    },
    grid: {
      show: false,
    },
    dataLabels: {
      enabled: false,
    },
    tooltip: {
      x: {
        formatter: (val: string, opts: any) => {
          const index = opts?.dataPointIndex ?? 0;
          return categories[index] || val;
        },
      },
      y: {
        formatter: (val: number) => {
          return formatNumber(val, 0);
        },
      },
    },
    plotOptions: {
      bar: {
        horizontal: false,
        dataLabels: {
          total: {
            enabled: notFifteen ? true : false,
            style: {
              fontSize: '12px',
              fontWeight: 400,
            },
          },
        },
      },
    },
  };
});
</script>
